import { DnsAttemptOutcome } from "@redotech/crdb-enums/dns/dns-attempt-outcome";
import { DnsFailureClass } from "@redotech/crdb-enums/dns/dns-failure-class";
import { DnsRecordType } from "@redotech/crdb-enums/dns/dns-record-type";
import { DnsSurface } from "@redotech/crdb-enums/dns/dns-surface";
/**
 * Vocabulary for the DNS requirements service. It reports which records a
 * surface needs and what they resolve to; it never writes DNS.
 */

export enum DnsAction {
  Add = "add",
  Replace = "replace",
  Delete = "delete",
}

/** What a caller switches on. Derived at read time, never stored. */
export enum RecordStatus {
  AlreadySetup = "already_setup",
  /** Submitted, not yet resolvable. */
  Pending = "pending",
  NeedsSetup = "needs_setup",
  /** Our resolver failed. Assert nothing. */
  Unknown = "unknown",
}

/** The distinctions the system branches on, underneath the bucket. */
export enum RecordDetail {
  /** The write is an add. */
  Missing = "missing",
  /** The write is a replace. */
  Misconfigured = "misconfigured",
  /** Cannot be written until an existing record is removed. */
  BlockedByConflict = "blocked_by_conflict",
  /** Submitted long ago and still absent — the provisioner claimed success. */
  Stalled = "stalled",
  /** No value can be computed. `prerequisite` says what is missing. */
  Unresolvable = "unresolvable",
}

export enum SurfaceStatus {
  /**
   * Every required DNS record resolves. NOT a claim that the product works —
   * ownership, provider acceptance and post-setup work are all separate.
   */
  Complete = "complete",
  /** Also covers waiting on propagation. */
  Incomplete = "incomplete",
  NotApplicable = "not_applicable",
  MissingPrerequisites = "missing_prerequisites",
}

/**
 * Short by default: every record here is one we are actively verifying, and a
 * wrong value cached for an hour is an hour of a merchant seeing it unfixed.
 */
export const DEFAULT_TTL_SECONDS = 300;

/**
 * Something that must happen before a value exists. The code is stable so the
 * UI can offer the fix; the message is its fallback text.
 */
export interface DnsPrerequisite {
  /** Open by design — surfaces name their own, e.g. "dkim_key_not_generated". */
  code: string;
  message: string;
}

export interface DnsRecordClaim {
  surface: DnsSurface;
  kind: string;
  /** Per team, so a record can be required for new merchants and optional for existing. */
  required: boolean;
  /** This surface's own verdict, which the grouped record's may outrank. */
  status: RecordStatus;
  detail?: RecordDetail;
}

/**
 * One surface's requirement, 1:1 with a stored row. `values` is the whole RRset
 * it needs, so contributors to one host and type all carry the same value.
 */
/**
 * What we want from the merchant for one record, and what changes it. Derived
 * from the record's state, the surface's change policy and any open attempt.
 */
export type RecordAsk =
  | { kind: "publish"; requiredBy?: Date }
  | { kind: "optional" }
  | { kind: "held_until_setup_finishes" }
  | { kind: "published_by_redo" }
  | { kind: "blocked"; prerequisite: DnsPrerequisite }
  /** Nothing to do: already resolving, or written and waiting on DNS. */
  | { kind: "satisfied" };

export interface DnsRequirement {
  surface: DnsSurface;
  kind: string;
  type: DnsRecordType;
  /** FQDN, so it survives a delegation boundary moving. */
  host: string;
  values: string[];
  /**
   * Our part of the value, knowable without reading DNS. Equal to `values` for
   * a record we own outright; a subset for one we share with the merchant.
   */
  adding: string[];
  /** Advice, never verified: a merchant raising it does not make us wrong. */
  ttlSeconds: number;
  required: boolean;
  /** Derived: "redo" when this host is inside a zone we were asked to serve. */
  managedBy: "redo" | "merchant";
  /** Needs a write the merchant can actually make. Excludes our own zone. */
  actionable: boolean;
  /**
   * Absent when no setup has been attempted. Compares our contribution, not the
   * whole value, so a merchant editing their own SPF never reads as our change.
   */
  sinceLastAttempt?: "new" | "changed" | "unchanged";
  /**
   * Where this record would move if they take the offer. Absent means it stays
   * put, or that no offer is open — delegationOffer says which.
   */
  delegatedHost?: string;
  /** "direct" while an offer is open means delegating would remove it. */
  onlyWhen?: "delegated" | "direct";
  /**
   * Which choice this record belongs to, while an offer is open. Absent means
   * no choice is open, so every actionable record applies.
   */
  appliesTo?: "direct" | "delegated" | "both";
  status: RecordStatus;
  detail?: RecordDetail;
  /** The single question a merchant-facing surface should render. */
  ask: RecordAsk;
  /** Set for Unresolvable — what must happen before we can name a value. */
  prerequisite?: DnsPrerequisite;
  conflictsWith?: string;
  observedValues?: string[];
  /** Operational, not merchant-facing. Explains RecordStatus.Unknown. */
  resolverError?: { code: string; at: Date };
}

export interface DnsRecord {
  type: DnsRecordType;
  /** FQDN, so it survives a delegation boundary moving. */
  host: string;
  /** A set: NS nameservers, or MX as "<preference> <exchange>" per exchange. */
  values: string[];
  /** Our part of it. Show this, not `values`, where the merchant owns the rest. */
  adding: string[];
  /** The shortest any contributor asked for. Advice, never verified. */
  ttlSeconds: number;
  /** Derived: true if any surface requires it. */
  required: boolean;
  /** What writing this record would be. "none" = blocked or unresolvable. */
  writeAction: DnsAction | "none";
  /** Derived from whether this record's zone is delegated to Redo. */
  managedBy: "redo" | "merchant";
  /** Needs a write the merchant can actually make. Excludes our own zone. */
  actionable: boolean;
  /** Absent when no setup has been attempted. Compares our contribution. */
  sinceLastAttempt?: "new" | "changed" | "unchanged";
  /**
   * Where this record would move if they take the offer. Absent means it stays
   * put, or that no offer is open — delegationOffer says which.
   */
  delegatedHost?: string;
  /** "direct" while an offer is open means delegating would remove it. */
  onlyWhen?: "delegated" | "direct";
  /** Which choice this record belongs to, while an offer is open. */
  appliesTo?: "direct" | "delegated" | "both";
  status: RecordStatus;
  detail?: RecordDetail;
  /** The single question a merchant-facing surface should render. */
  ask: RecordAsk;
  /** Set for Unresolvable — what must happen before we can name a value. */
  prerequisite?: DnsPrerequisite;
  conflictsWith?: string;
  observedValues?: string[];
  /** Operational, not merchant-facing. Explains RecordStatus.Unknown. */
  resolverError?: { code: string; at: Date };
  /**
   * Which surfaces this record serves — a zone gets one SPF however many want
   * it. Each carries its own verdict, since they can disagree.
   */
  satisfies: DnsRecordClaim[];
}

export interface SurfaceSummary {
  surface: DnsSurface;
  /** Actionable now. Excludes records merely awaiting propagation. */
  needsSetup: boolean;
  status: SurfaceStatus;
  /** Set for NotApplicable. Prerequisite text lives on each prerequisite. */
  reason?: string;
  /** Set for MissingPrerequisites. */
  prerequisites?: DnsPrerequisite[];
}

/**
 * Four nameservers reserved from our DNS provider. Assigned once per domain and
 * never changed: a merchant may paste them days after we showed them.
 */
export interface DelegationSet {
  id: string;
  nameservers: string[];
}

/** Hand us this subzone. What happens to existing records is on each record. */
export interface DelegationOffer {
  zone: string;
  nameservers: string[];
  /** Whose need this answers, so a narrowed read can drop someone else's. */
  surface: DnsSurface;
  /**
   * Records that would come into existence inside the zone, ours to publish.
   * They have no row to annotate, so they are listed here.
   */
  creates: { kind: string; type: DnsRecordType; host: string }[];
}

/** One thing the zone can or cannot do, and why we believe it. */
export interface ZoneCapabilityFact {
  /** Null = nothing has told us, so try it. False = a provider confirmed not. */
  supported: boolean | null;
  /** The provider's own code for refusing, so a screen can explain it. */
  reason?: string;
}

/** What the zone can do, so a caller knows whether to offer automation. */
export interface ZoneCapability {
  /** The DNS provider we recognized, for instructions. Null = unrecognized. */
  provider: string | null;
  /** When we last classified the nameservers. Null = never looked. */
  checkedAt: Date | null;
  automation: ZoneCapabilityFact;
  delegation: ZoneCapabilityFact;
}

interface DomainFacts {
  domain: string;
  lastObservedAt: Date | null;
  ownershipVerified: boolean;
  zone: ZoneCapability;
  /** The most recent provisioning run, for a banner above the records. */
  lastAttempt?: ProvisioningAttempt;
  /** Present when delegation is possible here and not already done. */
  delegationOffer?: DelegationOffer;
  /**
   * Zones whose NS resolves to the set we assigned, so they are ours to serve.
   * Empty when none — the offer names a zone before this does.
   */
  delegatedZones: string[];
  surfaces: SurfaceSummary[];
}

/** Enough to render a domain before any surface has been asked about it. */
export interface DomainSummary {
  domain: string;
  ownershipVerified: boolean;
  zone: ZoneCapability;
}

/** What the aggregate knows: one entry per surface requirement. */
export interface DomainState extends DomainFacts {
  requirements: DnsRequirement[];
}

/** What a merchant acts on: one entry per RRset. */
export interface DomainRequirements extends DomainFacts {
  records: DnsRecord[];
}

/** Who performed the write. Ours never surfaces as merchant-facing history. */
export type AttemptActor = "merchant" | "redo";

/**
 * Who reported an outcome. A browser closing its modal is optimistic and
 * unsigned; a provider's webhook is authoritative and arrives later.
 */
export type OutcomeSource = "merchant_browser" | "provider_webhook";

export interface AttemptFailure {
  class: DnsFailureClass;
  /** The provider's own code, for support and for grouping in dashboards. */
  code: string;
  message: string;
  /** What it refused. Defaults to automation, which is what a run was using. */
  capability?: "automation" | "delegation";
}

/**
 * One provisioning run. A session writes for the whole domain and returns one
 * outcome, so this is per attempt rather than per record.
 */
/** One record set as an attempt asked for it, frozen when the attempt opened. */
export interface AttemptRecord {
  host: string;
  type: DnsRecordType;
  values: string[];
  /** Our part, which is what a later change is judged against. */
  adding: string[];
  /** A provisioner may treat an optional record's failure as non-fatal. */
  required: boolean;
  /** Which arrangement it belongs to, so "either" stays separable on a retry. */
  appliesTo?: "direct" | "delegated" | "both";
  /** Absent on rows written before it was recorded; the default applies. */
  ttlSeconds?: number;
}

export interface ProvisioningAttempt {
  id: string;
  /** Whose flow it belongs to. */
  initiatedBy: DnsSurface;
  /** "redo" is a write into a zone we serve, which asks nothing of anyone. */
  actor: AttemptActor;
  initiatedAt: Date;
  /** Immutable, so a late callback is matched against what we asked for. */
  requested: AttemptRecord[];
  outcome: DnsAttemptOutcome;
  settledAt: Date | null;
  failure?: AttemptFailure;
}
